/**
 * 提现（出款）与审批流。
 *
 * ```
 * draft ──提交──▶ pending_approval ──批准──▶ approved ──执行──▶ processing
 *   │                  │                                          │
 *   └──撤销──▶ cancelled            ┌──标记已付──▶ paid
 *                      └──驳回──▶ rejected  └──标记失败──▶ failed
 * ```
 *
 * 三条不可让步的规则，界面上都直接写出来了 —— 被拒时用户要知道卡在哪：
 *
 * 1. **审批人不能是发起人本人**（四眼原则），发起人从审计日志里查
 * 2. **审批人必须是该组织的严格上级**，自己批自己的钱是最典型的资损路径
 * 3. **账本分录只在「已出款」那一刻落**，之前的状态靠在途提现从可提额度里扣减
 *
 * 创建强制幂等：这是控制台里唯一「重复执行就会多付一次钱」的接口，
 * 而且没有任何唯一约束兜底。所以这里每次打开表单都生成一个新的幂等键，
 * **提交失败重试时复用同一个键** —— 网关超时后重试正是它存在的意义。
 */

import { useMemo, useState } from 'react';
import { newIdempotencyKey, request } from '../lib/api.ts';
import { useAsync, usePagedList, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge, Button, Card, CopyId, DetailGrid, ErrorBox, Field, Loading, Modal, Money,
  Select, StatusBadge, TextInput,
} from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatMinor, toMinor } from '../lib/money.ts';
import { formatDateTime } from '../lib/format.ts';
import type { Funds, Payout } from '../lib/types.ts';

const PAYOUT_STATUSES = [
  { value: 'draft', label: '草稿' },
  { value: 'pending_approval', label: '待审批' },
  { value: 'approved', label: '已批准' },
  { value: 'processing', label: '出款中' },
  { value: 'paid', label: '已出款' },
  { value: 'rejected', label: '已驳回' },
  { value: 'failed', label: '失败' },
  { value: 'cancelled', label: '已撤销' },
];

const METHODS = [
  { value: 'bank_transfer', label: '银行转账' },
  { value: 'internal_transfer', label: '内部划转' },
  { value: 'crypto', label: '加密货币' },
  { value: 'manual', label: '线下手工' },
];

export function PayoutsPage() {
  const session = useSession();
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const list = usePagedList<Payout>('/v1/payouts', {
    status: status === '' ? null : status,
  });

  // 受分组约束的成员只能看本组商户的提现，不能发起 —— 服务端只读闸会 403，按钮跟它对齐
  const canCreate =
    session.can('operator') && session.me.org.status === 'active' && !session.isChannelScoped;

  const columns: readonly Column<Payout>[] = [
    { key: 'id', header: '单号', render: (row) => <CopyId id={row.id} /> },
    { key: 'org', header: '组织', render: (row) => row.org_name ?? row.org_id },
    { key: 'amount', header: '金额', numeric: true, render: (row) => (
      <Money value={row.amount} currency={row.currency} />
    ) },
    { key: 'method', header: '方式', render: (row) => (
      <span className="cell-muted">
        {METHODS.find((m) => m.value === row.method)?.label ?? row.method}
      </span>
    ) },
    { key: 'status', header: '状态', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'batch', header: '关联批次', render: (row) => (
      row.batch_id === null ? <span className="cell-muted">—</span> : <CopyId id={row.batch_id} />
    ) },
    { key: 'created', header: '发起时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
    { key: 'paid', header: '出款时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.paid_at)}</span>
    ) },
  ];

  return (
    <>
      <div className="row">
        <h1>提现</h1>
        <span style={{ flex: 1 }} />
        {canCreate && <Button variant="primary" onClick={() => { setCreating(true); }}>发起提现</Button>}
      </div>

      <div className="filters">
        <Field label="状态">
          <Select value={status} onChange={setStatus} placeholder="全部" options={PAYOUT_STATUSES} />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          onRowClick={(row) => { setSelected(row.id); }}
          empty="还没有提现单"
          emptyHint="发起提现前需要账上有可提现余额 —— 可提 = 可用 − 在途提现。"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>

      {creating && (
        <CreatePayoutModal
          onClose={() => { setCreating(false); }}
          onDone={() => { setCreating(false); list.reload(); }}
        />
      )}
      {selected !== null && (
        <PayoutModal
          id={selected}
          onClose={() => { setSelected(null); }}
          onChanged={() => { list.reload(); }}
        />
      )}
    </>
  );
}

function CreatePayoutModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const session = useSession();
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [destRef, setDestRef] = useState('');
  const [draft, setDraft] = useState(false);

  // 幂等键在打开表单时生成一次，整个生命周期内不变：
  // 提交超时后重试要带同一个键，否则会开出第二张等额提现单
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);

  const funds = useAsync<Funds>(
    '/v1/ledger/funds',
    { query: { org_id: session.me.org.id, currency } },
    [currency, session.me.org.id],
  );

  const [amountError, setAmountError] = useState<string | null>(null);

  const create = useMutation(async () => {
    // 金额在提交前就转成最小单位：让「小数位超了」这类错误在本地立刻暴露，
    // 而不是发一次请求再被后端拒绝
    const minor = toMinor(amount, currency);
    if (minor <= 0n) throw new Error('提现金额必须大于 0');
    return await request<Payout>('/v1/payouts', {
      method: 'POST',
      idempotencyKey,
      body: {
        currency,
        amount: minor.toString(),
        method,
        dest_ref: destRef,
        ...(draft ? { draft: true } : {}),
      },
    });
  });

  const withdrawable = funds.data?.withdrawable.withdrawable ?? null;

  return (
    <Modal
      title="发起提现"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={create.pending || amount === '' || destRef === '' || amountError !== null}
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {create.pending ? '提交中…' : draft ? '存为草稿' : '提交审批'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      {funds.data !== null && (
        <div className="alert" data-tone={funds.data.withdrawable.over_committed ? 'bad' : 'info'}>
          <span className="alert-title">
            当前可提现 {formatMinor(withdrawable, currency)} {currency}
          </span>
          <span>
            可用 {formatMinor(funds.data.available, currency)} − 在途提现{' '}
            {formatMinor(funds.data.withdrawable.pending_payouts, currency)}。
            待结算的 {formatMinor(funds.data.pending, currency)} 还没到账期，不能提。
          </span>
        </div>
      )}

      <div className="grid cols-2">
        <Field label="币种">
          <TextInput value={currency} onChange={(value) => { setCurrency(value.toUpperCase()); }} />
        </Field>
        <Field label="金额" hint={`按 ${currency} 的正常写法填，例如 1234.56`}>
          <TextInput
            value={amount}
            numeric
            onChange={(value) => {
              setAmount(value);
              if (value === '') { setAmountError(null); return; }
              try {
                toMinor(value, currency);
                setAmountError(null);
              } catch (cause) {
                setAmountError(cause instanceof Error ? cause.message : '金额不合法');
              }
            }}
          />
        </Field>
      </div>

      {amountError !== null && (
        <div className="alert" data-tone="warn"><span className="alert-title">{amountError}</span></div>
      )}

      <Field label="出款方式">
        <Select value={method} onChange={setMethod} options={METHODS} />
      </Field>

      <Field
        label="收款目的地引用"
        hint="填银行账户记录的 id 或地址句柄，绝不要在这里填明文卡号/账号。"
      >
        <TextInput value={destRef} onChange={setDestRef} placeholder="bank_acct_xxx" />
      </Field>

      <label className="checkbox">
        <input type="checkbox" checked={draft} onChange={(e) => { setDraft(e.target.checked); }} />
        先存为草稿，稍后再提交审批
      </label>

      <div className="alert" data-tone="info">
        <span className="alert-title">审批需要另一个人</span>
        <span>四眼原则：审批人不能是发起人本人，而且必须是该组织的严格上级。</span>
      </div>
    </Modal>
  );
}

function PayoutModal({
  id, onClose, onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const session = useSession();
  const detail = useAsync<Payout>(`/v1/payouts/${id}`);
  const [reason, setReason] = useState('');

  const act = useMutation(async (action: string, body?: unknown) => {
    const result = await request<Payout>(`/v1/payouts/${id}/${action}`, {
      method: 'POST',
      ...(body === undefined ? {} : { body }),
    });
    detail.reload();
    onChanged();
    return result;
  });

  if (detail.error !== null) {
    return <Modal title="提现单" onClose={onClose}><ErrorBox error={detail.error} /></Modal>;
  }
  if (detail.data === null) {
    return <Modal title="提现单" onClose={onClose}><Loading /></Modal>;
  }

  const payout = detail.data;
  // 所有动作对受分组约束的成员都不可用：他被放开的只是「看」（见服务端 channelScopedReadOnlyMw）
  const canAct = !session.isChannelScoped;
  const isPlatformAdmin = canAct && session.isPlatform && session.can('admin');
  const canApprove = canAct && session.can('admin') && payout.org_id !== session.me.org.id;

  // 允许的迁移，与后端 TRANSITIONS 表一致
  const canSubmit = payout.status === 'draft';
  const canCancel = ['draft', 'pending_approval', 'approved'].includes(payout.status);
  const canDecide = payout.status === 'pending_approval' && canApprove;
  const canExecute = payout.status === 'approved' && isPlatformAdmin;
  const canFinish = payout.status === 'processing' && isPlatformAdmin;

  return (
    <Modal title="提现单" onClose={onClose} wide>
      <ErrorBox error={act.error} />

      <DetailGrid rows={[
        ['单号', <CopyId key="id" id={payout.id} full />],
        ['组织', <span key="o">{payout.org_name ?? payout.org_id}</span>],
        ['金额', <Money key="a" value={payout.amount} currency={payout.currency} />],
        ['方式', <span key="m">{METHODS.find((item) => item.value === payout.method)?.label ?? payout.method}</span>],
        ['收款目的地', <span key="d" className="mono">{payout.dest_ref}</span>],
        ['状态', <StatusBadge key="s" status={payout.status} />],
        payout.batch_id === null ? null : ['关联批次', <CopyId key="b" id={payout.batch_id} full />],
        ['发起时间', <span key="c" className="mono">{formatDateTime(payout.created_at)}</span>],
        payout.approved_at === null ? null
          : ['批准时间', <span key="ap" className="mono">{formatDateTime(payout.approved_at)}</span>],
        payout.paid_at === null ? null
          : ['出款时间', <span key="p" className="mono">{formatDateTime(payout.paid_at)}</span>],
        payout.last_error === null ? null
          : ['最后错误', <span key="e" className="dr">{payout.last_error}</span>],
      ]} />

      {(canDecide || payout.status === 'processing') && (
        <Field label="驳回理由 / 失败原因">
          <TextInput value={reason} onChange={setReason} placeholder="会写进审计日志" />
        </Field>
      )}

      <div className="row">
        {canAct && canSubmit && (
          <Button variant="primary" disabled={act.pending} onClick={() => { void act.run('submit'); }}>
            提交审批
          </Button>
        )}
        {canDecide && (
          <>
            <Button variant="primary" disabled={act.pending} onClick={() => { void act.run('approve'); }}>
              批准
            </Button>
            <Button
              variant="danger"
              disabled={act.pending || reason.trim() === ''}
              onClick={() => { void act.run('reject', { reason }); }}
            >
              驳回
            </Button>
          </>
        )}
        {canExecute && (
          <Button variant="primary" disabled={act.pending} onClick={() => { void act.run('execute'); }}>
            执行出款
          </Button>
        )}
        {canFinish && (
          <>
            <Button variant="primary" disabled={act.pending} onClick={() => { void act.run('mark-paid'); }}>
              标记已出款
            </Button>
            <Button
              variant="danger"
              disabled={act.pending || reason.trim() === ''}
              onClick={() => { void act.run('mark-failed', { error: reason }); }}
            >
              标记失败
            </Button>
          </>
        )}
        {canAct && canCancel && session.can('admin') && (
          <Button disabled={act.pending} onClick={() => { void act.run('cancel'); }}>撤销</Button>
        )}
      </div>

      {canFinish && (
        <div className="alert" data-tone="warn">
          <span className="alert-title">「标记已出款」会落账本分录</span>
          <span>
            这一步代表钱真的离开了归集户。落账前系统会持锁复核一次可用余额，
            不足会直接拒绝 —— 但一旦落账就不可撤销，请确认银行侧确实已经打款成功。
          </span>
        </div>
      )}

      {payout.audit_trail !== undefined && payout.audit_trail.length > 0 && (
        <div className="stack tight">
          <h3>审批轨迹</h3>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr><th>动作</th><th>操作人</th><th>时间</th></tr>
              </thead>
              <tbody>
                {payout.audit_trail.map((entry) => (
                  <tr key={entry.id}>
                    <td><Badge tone="neutral">{entry.action.replace('payout_request.', '')}</Badge></td>
                    <td>{entry.actor_user_id === null ? '系统' : <CopyId id={entry.actor_user_id} />}</td>
                    <td className="cell-muted mono">{formatDateTime(entry.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
